import { Download01 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useFlagQaData, useFlagQaMerchants } from "@/api/admin/flag-qa";
import { Table, TableCard } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FlagQaCategoryComparison } from "./components/FlagQaCategoryComparison";
import { FlagQaComparisonKpis } from "./components/FlagQaComparisonKpis";
import { FlagQaF1Legend } from "./components/FlagQaF1Legend";
import { FlagQaFilters } from "./components/FlagQaFilters";
import { FlagQaKpiCards } from "./components/FlagQaKpiCards";
import { FlagQaRiskAttention } from "./components/FlagQaRiskAttention";
import { FlagQaSummaryTable } from "./components/FlagQaSummaryTable";
import { FlagQaTrendChart } from "./components/FlagQaTrendChart";
import {
  CHART_COLORS,
  type ComparisonPreset,
  type FlagQaRecord,
  type SummaryRow,
  type TimeMode,
  type TrendGranularity,
  type WeekStartDay,
} from "./flag-qa-types";
import {
  buildCategoryComparison,
  buildSummary,
  calculatePeriodSummary,
  filterByDateRange,
  getComparisonDateRange,
  groupByPeriod,
} from "./flag-qa-utils";

const exportToCSV = (data: FlagQaRecord[], filename: string) => {
  const headers = [
    "ID",
    "Confidence Index",
    "Explanation",
    "Visual Trigger",
    "Transcript Trigger",
    "Video ID",
    "Risk Type",
    "Flag Created At",
    "Action Taken",
    "Feedback Reason",
    "Feedback Text",
    "Original Flag",
    "New Flag",
    "Merchant ID",
    "Edit Created At",
  ];

  const rows = data.map((record) => [
    record.id,
    record.confidenceIndex,
    `"${record.explanation.replace(/"/g, '""')}"`,
    `"${(record.visualEvidence || "").replace(/"/g, '""')}"`,
    `"${(record.transcriptEvidence || "").replace(/"/g, '""')}"`,
    record.videoId.replace("VID-", ""),
    record.riskTypeShort,
    record.flagCreatedAt,
    record.actionTaken || "",
    record.feedbackReason || "",
    `"${(record.feedbackText || "").replace(/"/g, '""')}"`,
    record.originalFlag || "",
    record.newFlag || "",
    record.merchantId,
    record.editCreatedAt || "",
  ]);

  const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportSummaryToCSV = (summary: SummaryRow[], filename: string) => {
  const headers = [
    "Category",
    "Total Flags",
    "FP (flag_dismissed)",
    "TP (flag_is_correct)",
    "FN (add_flag)",
    "Accuracy (%)",
    "Recall (%)",
    "Precision (%)",
    "F1 (%)",
  ];

  const rows = summary.map((row) => [
    row.category,
    row.total,
    row.fp,
    row.tp,
    row.fn,
    row.accuracy,
    row.recall,
    row.precision,
    row.f1,
  ]);

  const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const AdminFlagQaTable = () => {
  const { data: merchantsData } = useFlagQaMerchants();
  const merchants = merchantsData?.map((m) => m.merchantId) || [];

  // Merchant selection (multi-select)
  const [selectedMerchants, setSelectedMerchants] = useState<string[]>([]);

  // Time mode
  const [timeMode, setTimeMode] = useState<TimeMode>("single");

  // Single period state
  const [quickRange, setQuickRange] = useState<string>("30");
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 29);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split("T")[0];
  });

  // Compare mode state
  const [comparisonPreset, setComparisonPreset] = useState<ComparisonPreset>("wow");
  const [periodAStart, setPeriodAStart] = useState<string>("");
  const [periodAEnd, setPeriodAEnd] = useState<string>("");
  const [periodBStart, setPeriodBStart] = useState<string>("");
  const [periodBEnd, setPeriodBEnd] = useState<string>("");

  // Trend mode state
  const [trendGranularity, setTrendGranularity] = useState<TrendGranularity>("weekly");
  const [weekStartDay, setWeekStartDay] = useState<WeekStartDay>("MON");
  const [trendStartDate, setTrendStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 90);
    return date.toISOString().split("T")[0];
  });
  const [trendEndDate, setTrendEndDate] = useState<string>(() => {
    return new Date().toISOString().split("T")[0];
  });

  // Min flags threshold for trend
  const [minFlagsThreshold, setMinFlagsThreshold] = useState(0);

  // Raw data table filters
  const [flagTypeFilter, setFlagTypeFilter] = useState<string>("all");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100;

  // Calculate API date range based on mode
  const apiDateRange = useMemo(() => {
    if (timeMode === "single" || timeMode === "risks") {
      return { startDate, endDate };
    }
    if (timeMode === "compare") {
      if (comparisonPreset === "custom") {
        // Use the full range of both periods
        const starts = [periodAStart, periodBStart].filter(Boolean);
        const ends = [periodAEnd, periodBEnd].filter(Boolean);
        return {
          startDate: starts.length > 0 ? starts.sort()[0] : undefined,
          endDate: ends.length > 0 ? ends.sort().reverse()[0] : undefined,
        };
      }
      const ranges = getComparisonDateRange(comparisonPreset);
      return {
        startDate: ranges.periodB.start,
        endDate: ranges.periodA.end,
      };
    }
    // Trend mode
    return { startDate: trendStartDate, endDate: trendEndDate };
  }, [
    timeMode,
    startDate,
    endDate,
    comparisonPreset,
    periodAStart,
    periodAEnd,
    periodBStart,
    periodBEnd,
    trendStartDate,
    trendEndDate,
  ]);

  // Pass selected merchants to API (undefined for all)
  const merchantIdsForApi = selectedMerchants.length > 0 ? selectedMerchants : undefined;

  const { data: rawData, isLoading } = useFlagQaData(merchantIdsForApi, apiDateRange.startDate, apiDateRange.endDate);
  const data = rawData ?? [];

  // Flag types for filter dropdown
  const flagTypes = useMemo(() => {
    const types = new Set(
      data.map((r) => r.newFlag).filter((t): t is string => typeof t === "string" && t.trim().length > 0),
    );
    return Array.from(types)
      .filter((t) => typeof t === "string")
      .sort();
  }, [data]);

  // Filtered data for raw data table
  const filteredData = useMemo(() => {
    return data.filter((record) => {
      if (flagTypeFilter !== "all" && record.newFlag !== flagTypeFilter) return false;
      if (actionFilter !== "all" && record.actionTaken !== actionFilter) return false;
      return true;
    });
  }, [data, flagTypeFilter, actionFilter]);

  // Reset to page 1 when filters change
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [data, flagTypeFilter, actionFilter]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  // Summary for category table
  const summary = useMemo(() => buildSummary(data), [data]);

  // Period summary for KPI cards (single mode)
  const periodSummary = useMemo(() => calculatePeriodSummary(data), [data]);

  // Comparison data
  const comparisonData = useMemo(() => {
    if (timeMode !== "compare") return null;

    let periodARangeStart: string;
    let periodARangeEnd: string;
    let periodBRangeStart: string;
    let periodBRangeEnd: string;

    if (comparisonPreset === "custom") {
      periodARangeStart = periodAStart;
      periodARangeEnd = periodAEnd;
      periodBRangeStart = periodBStart;
      periodBRangeEnd = periodBEnd;
    } else {
      const ranges = getComparisonDateRange(comparisonPreset);
      periodARangeStart = ranges.periodA.start;
      periodARangeEnd = ranges.periodA.end;
      periodBRangeStart = ranges.periodB.start;
      periodBRangeEnd = ranges.periodB.end;
    }

    if (!periodARangeStart || !periodARangeEnd || !periodBRangeStart || !periodBRangeEnd) {
      return null;
    }

    const periodAData = filterByDateRange(data, periodARangeStart, periodARangeEnd);
    const periodBData = filterByDateRange(data, periodBRangeStart, periodBRangeEnd);

    const categorySummaryA = buildSummary(periodAData);
    const categorySummaryB = buildSummary(periodBData);

    return {
      periodA: {
        summary: calculatePeriodSummary(periodAData),
        categorySummary: categorySummaryA,
        label: "Current",
        dateRange: `${periodARangeStart} to ${periodARangeEnd}`,
      },
      periodB: {
        summary: calculatePeriodSummary(periodBData),
        categorySummary: categorySummaryB,
        label: "Previous",
        dateRange: `${periodBRangeStart} to ${periodBRangeEnd}`,
      },
      categoryComparison: buildCategoryComparison(categorySummaryA, categorySummaryB),
    };
  }, [timeMode, comparisonPreset, periodAStart, periodAEnd, periodBStart, periodBEnd, data]);

  // Trend data
  const trendData = useMemo(() => {
    if (timeMode !== "trend") return [];
    return groupByPeriod(data, trendGranularity, weekStartDay);
  }, [timeMode, data, trendGranularity, weekStartDay]);

  // Top risks to address
  const topRisksToAddress = useMemo(() => {
    return summary
      .map((row) => ({
        category: row.category,
        issueCount: row.fp + row.fn,
        fp: row.fp,
        fn: row.fn,
      }))
      .filter((row) => row.issueCount > 0)
      .sort((a, b) => b.issueCount - a.issueCount)
      .slice(0, 3);
  }, [summary]);

  // Chart data
  const chartData = summary.map((row) => ({
    category: row.category,
    "FP (flag_dismissed)": row.fp,
    "TP (flag_is_correct)": row.tp,
    "FN (add_flag)": row.fn,
  }));

  // Quick range handler
  const handleQuickRangeChange = (value: string) => {
    setQuickRange(value);

    if (value === "all" || value === "custom") {
      setStartDate("");
      setEndDate("");
      return;
    }

    const today = new Date();
    const end = today.toISOString().split("T")[0];
    const days = Number.parseInt(value, 10);
    const start = new Date(today);
    start.setDate(start.getDate() - days + 1);

    setStartDate(start.toISOString().split("T")[0]);
    setEndDate(end);
  };

  // Comparison preset handler
  const handleComparisonPresetChange = (preset: ComparisonPreset) => {
    setComparisonPreset(preset);

    if (preset !== "custom") {
      const ranges = getComparisonDateRange(preset);
      setPeriodAStart(ranges.periodA.start);
      setPeriodAEnd(ranges.periodA.end);
      setPeriodBStart(ranges.periodB.start);
      setPeriodBEnd(ranges.periodB.end);
    }
  };

  const merchantLabel = selectedMerchants.length === 0 ? "all" : selectedMerchants.join("-");

  return (
    <div className="flex flex-col gap-6">
      {/* F1 Scale Legend */}
      <FlagQaF1Legend />

      {/* Filters */}
      <FlagQaFilters
        merchants={merchants}
        selectedMerchants={selectedMerchants}
        onSelectedMerchantsChange={setSelectedMerchants}
        timeMode={timeMode}
        onTimeModeChange={setTimeMode}
        quickRange={quickRange}
        onQuickRangeChange={handleQuickRangeChange}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        comparisonPreset={comparisonPreset}
        onComparisonPresetChange={handleComparisonPresetChange}
        periodAStart={periodAStart}
        periodAEnd={periodAEnd}
        periodBStart={periodBStart}
        periodBEnd={periodBEnd}
        onPeriodAStartChange={setPeriodAStart}
        onPeriodAEndChange={setPeriodAEnd}
        onPeriodBStartChange={setPeriodBStart}
        onPeriodBEndChange={setPeriodBEnd}
        trendGranularity={trendGranularity}
        onTrendGranularityChange={setTrendGranularity}
        trendStartDate={trendStartDate}
        trendEndDate={trendEndDate}
        onTrendStartDateChange={setTrendStartDate}
        onTrendEndDateChange={setTrendEndDate}
        weekStartDay={weekStartDay}
        onWeekStartDayChange={setWeekStartDay}
      />

      {isLoading ? (
        <div className="text-center py-8 text-secondary">Loading...</div>
      ) : data.length === 0 ? (
        <div className="text-center py-8 text-tertiary">No data for the selected filters.</div>
      ) : (
        <>
          {/* KPIs based on time mode */}
          {timeMode === "single" && <FlagQaKpiCards summary={periodSummary} />}

          {timeMode === "compare" && comparisonData && (
            <>
              <FlagQaComparisonKpis periodA={comparisonData.periodA} periodB={comparisonData.periodB} />

              {/* Category Comparison Table */}
              <FlagQaCategoryComparison data={comparisonData.categoryComparison} />

              {/* Period A Summary Table */}
              <FlagQaSummaryTable
                title={`Flags by Category & Action - ${comparisonData.periodA.label} (${comparisonData.periodA.dateRange})`}
                summary={comparisonData.periodA.categorySummary}
                onExport={() =>
                  exportSummaryToCSV(
                    comparisonData.periodA.categorySummary,
                    `flags_summary_periodA_${merchantLabel}_${Date.now()}.csv`,
                  )
                }
                exportLabel="Download Period A CSV"
              />

              {/* Period B Summary Table */}
              <FlagQaSummaryTable
                title={`Flags by Category & Action - ${comparisonData.periodB.label} (${comparisonData.periodB.dateRange})`}
                summary={comparisonData.periodB.categorySummary}
                onExport={() =>
                  exportSummaryToCSV(
                    comparisonData.periodB.categorySummary,
                    `flags_summary_periodB_${merchantLabel}_${Date.now()}.csv`,
                  )
                }
                exportLabel="Download Period B CSV"
              />
            </>
          )}

          {timeMode === "trend" && (
            <FlagQaTrendChart
              data={trendData}
              minFlagsThreshold={minFlagsThreshold}
              onMinFlagsThresholdChange={setMinFlagsThreshold}
            />
          )}

          {timeMode === "risks" && <FlagQaRiskAttention data={data} />}

          {/* Top Risks to Address (only in single mode) */}
          {timeMode === "single" && topRisksToAddress.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-primary">Top 3 Risks to Address</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {topRisksToAddress.map((risk, index) => (
                  <TableCard.Root key={risk.category} className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="text-sm font-medium text-tertiary">#{index + 1}</div>
                      <Badge color="error" size="sm">
                        {risk.issueCount} issues
                      </Badge>
                    </div>
                    <div className="text-lg font-bold text-primary mb-3">{risk.category}</div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-secondary">False Positives (FP)</span>
                        <span className="text-sm font-semibold text-red-400">{risk.fp}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-secondary">Missed Flags (FN)</span>
                        <span className="text-sm font-semibold text-red-500">{risk.fn}</span>
                      </div>
                    </div>
                  </TableCard.Root>
                ))}
              </div>
            </div>
          )}

          {/* Summary Table (only in single mode) */}
          {timeMode === "single" && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-primary">Flags by Category & Action</h3>
                <Button
                  size="sm"
                  onClick={() => exportSummaryToCSV(summary, `flags_summary_${merchantLabel}_${Date.now()}.csv`)}
                  className="flex items-center gap-2"
                >
                  <Download01 className="size-4" />
                  Download Summary CSV
                </Button>
              </div>
              <TableCard.Root>
                <div className="overflow-x-auto">
                  <Table>
                    <Table.Header>
                      <Table.Head id="category" label="Category" isRowHeader />
                      <Table.Head id="total" label="Total" />
                      <Table.Head id="fp" label="FP" />
                      <Table.Head id="tp" label="TP" />
                      <Table.Head id="fn" label="FN" />
                      <Table.Head id="accuracy" label="Accuracy %" />
                      <Table.Head id="recall" label="Recall %" />
                      <Table.Head id="precision" label="Precision %" />
                      <Table.Head id="f1" label="F1 %" />
                    </Table.Header>
                    <Table.Body items={summary}>
                      {(row) => (
                        <Table.Row id={row.category}>
                          <Table.Cell>{row.category}</Table.Cell>
                          <Table.Cell>{row.total}</Table.Cell>
                          <Table.Cell>{row.fp}</Table.Cell>
                          <Table.Cell>{row.tp}</Table.Cell>
                          <Table.Cell>{row.fn}</Table.Cell>
                          <Table.Cell>{row.accuracy.toFixed(1)}</Table.Cell>
                          <Table.Cell>{row.recall.toFixed(1)}</Table.Cell>
                          <Table.Cell>{row.precision.toFixed(1)}</Table.Cell>
                          <Table.Cell>{row.f1.toFixed(1)}</Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table>
                </div>
              </TableCard.Root>
            </div>
          )}

          {/* Chart (only in single mode) */}
          {timeMode === "single" && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-primary">Flag Distribution</h3>
              <TableCard.Root className="p-4">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                    <XAxis dataKey="category" stroke={CHART_COLORS.axis} hide />
                    <YAxis stroke={CHART_COLORS.axis} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: CHART_COLORS.tooltipBg,
                        border: `1px solid ${CHART_COLORS.tooltipBorder}`,
                      }}
                    />
                    <Bar dataKey="FN (add_flag)" fill={CHART_COLORS.fn} />
                    <Bar dataKey="FP (flag_dismissed)" fill={CHART_COLORS.fp} />
                    <Bar dataKey="TP (flag_is_correct)" fill={CHART_COLORS.tp} />
                  </BarChart>
                </ResponsiveContainer>
              </TableCard.Root>
            </div>
          )}

          {/* Raw Data */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-primary">Raw Data</h3>
              <Button
                size="sm"
                onClick={() => exportToCSV(filteredData, `flags_${merchantLabel}_${Date.now()}.csv`)}
                className="flex items-center gap-2"
              >
                <Download01 className="size-4" />
                Download CSV
              </Button>
            </div>

            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block text-secondary">Flag Type</label>
                <Select value={flagTypeFilter} onValueChange={setFlagTypeFilter}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="all" className="text-white focus:bg-gray-700">
                      All
                    </SelectItem>
                    {flagTypes.map((type) => (
                      <SelectItem key={type} value={type} className="text-white focus:bg-gray-700">
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block text-secondary">Action</label>
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700 text-white">
                    <SelectItem value="all" className="text-white focus:bg-gray-700">
                      All
                    </SelectItem>
                    <SelectItem value="flag_dismissed" className="text-white focus:bg-gray-700">
                      FP (flag_dismissed)
                    </SelectItem>
                    <SelectItem value="flag_is_correct" className="text-white focus:bg-gray-700">
                      TP (flag_is_correct)
                    </SelectItem>
                    <SelectItem value="add_flag" className="text-white focus:bg-gray-700">
                      FN (add_flag)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <TableCard.Root>
              <div className="max-h-96 overflow-auto">
                <Table>
                  <Table.Header>
                    <Table.Head id="videoId" label="Video ID" isRowHeader />
                    <Table.Head id="riskType" label="Risk Type" />
                    <Table.Head id="confidence" label="Confidence" />
                    <Table.Head id="newFlag" label="New Flag" />
                    <Table.Head id="actionTaken" label="Action" />
                    <Table.Head id="createdAt" label="Created At" />
                  </Table.Header>
                  <Table.Body items={paginatedData}>
                    {(record) => (
                      <Table.Row id={record.id}>
                        <Table.Cell className="whitespace-nowrap font-mono text-xs">
                          {record.videoId.replace("VID-", "")}
                        </Table.Cell>
                        <Table.Cell>{record.riskTypeShort}</Table.Cell>
                        <Table.Cell>{record.confidenceIndex.toFixed(2)}</Table.Cell>
                        <Table.Cell>
                          {record.newFlag && (
                            <Badge color="blue-light" size="sm">
                              {record.newFlag}
                            </Badge>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {record.actionTaken && (
                            <Badge
                              color={
                                record.actionTaken === "flag_is_correct"
                                  ? "success"
                                  : record.actionTaken === "flag_dismissed"
                                    ? "orange"
                                    : "error"
                              }
                              size="sm"
                            >
                              {record.actionTaken}
                            </Badge>
                          )}
                        </Table.Cell>
                        <Table.Cell className="whitespace-nowrap">
                          {new Date(record.flagCreatedAt).toLocaleDateString()}
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>
              </div>
            </TableCard.Root>

            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-tertiary">
                Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, filteredData.length)} of{" "}
                {filteredData.length} records
                {filteredData.length !== data.length && ` (filtered from ${data.length})`}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    isDisabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-secondary">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    isDisabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
